import {
  MONITOR_BITS,
  PATIENT_PARAMETER_BITS,
  PARAMETER_OPTION_BITS,
  PATIENT_CATEGORY_BITS,
  USAGE_CATEGORY_BITS,
} from "constants/category-bits";

const PATIENT_CATEGORY_FULL_MASK = Object.keys(PATIENT_CATEGORY_BITS).reduce(
  (acc, key) => acc | PATIENT_CATEGORY_BITS[key],
  0,
);

const USAGE_CATEGORY_FULL_MASK = Object.keys(USAGE_CATEGORY_BITS).reduce(
  (acc, key) => acc | USAGE_CATEGORY_BITS[key],
  0,
);

const inferReviewCategory = (patientParameter) => {
  const foundKey = Object.keys(PATIENT_PARAMETER_BITS).find(
    (key) => patientParameter & PATIENT_PARAMETER_BITS[key],
  );
  if (foundKey) {
    return `${foundKey}-accessories`;
  }
  return "uncategorized";
};

const matchesOptionFilter = (part, parameterOptionFilter) => {
  // 7/21/26 - Special case if parameterOptionFilter is undefined or empty string, we want to match all parts even if part.parameterOption is undefined or 0. This is for the case of a monitor that has no parameter options, like the Vista 120.
  if (!parameterOptionFilter) {
    return true;
  }

  // check if part has the parameter options specified by the filter
  const filterBits =
    typeof parameterOptionFilter !== "undefined" && parameterOptionFilter !== ""
      ? Object.keys(PARAMETER_OPTION_BITS).reduce(
          (acc, key) =>
            acc |
            (key.indexOf(parameterOptionFilter) === 0
              ? PARAMETER_OPTION_BITS[key]
              : 0),
          0,
        )
      : 0xffffffff;

  return part.parameterOption & filterBits;
};

// Helpers
export const isPartNo = (partNo) => /^M|\d{7}/.test(partNo);
export const createPart = (partData) =>
  Object.assign(new Part(), partData, {
    reviewCategory:
      partData.reviewCategory || inferReviewCategory(partData.patientParameter),
  });

// TODO; make this filter parts by monitor Id
export const createPartsCollection = (parts, monitor) =>
  Object.assign(new PartsCollection(), {
    parts: parts.filter((part) => part.monitors & MONITOR_BITS[monitor.id]),
    filterSequence: monitor.filterSequence || [
      "patientParameter",
      "parameterOption",
      "patientCategory",
      "usageCategory",
    ],
  });

export class Part {
  isPairedNewOption(parameterOption) {
    return (
      typeof this.pairedPart !== "undefined" &&
      this.newParameterOption === parameterOption
    );
  }

  isPaired(otherPart) {
    return (
      this.pairedParts &&
      this.pairedParts.some(
        (pairing) =>
          pairing === otherPart.partNo ||
          pairing === otherPart.group ||
          (otherPart.type === "kit" &&
            otherPart.includedParts.includes(pairing)) ||
          (otherPart.includedParts &&
            otherPart.includedParts.includes(pairing)),
      )
    );
  }

  isOpaqueKit() {
    return this.type === "kit" && this.imageId && this.imageId !== "000";
  }

  fitsCable(cablePart) {
    return (
      this.cables === "any" ||
      !cablePart ||
      (Array.isArray(this.cables) && this.cables.includes(cablePart.partNo))
    );
  }

  parseComment(filterChoices) {
    // check for conditional comments like
    // 'Quantity 2??12-lead (iec1 europe)||12-lead (iec2 aha/us)'
    if (!this.commentLogic) {
      return;
    }

    this.showComment = true;
    if (typeof this.commentLogic === "boolean") {
      return;
    }
    const conditions = this.commentLogic.split("??");
    if (conditions.length === 1) {
      return;
    }
    // for now, we'll just worry about the OR
    const filtersChosen = Array.from(filterChoices.entries());
    const options = conditions[1].split("||");
    this.showComment = filtersChosen.some((entry) =>
      options.includes(entry[1]),
    );
  }

  clearScratchData() {
    this.scratchData = {};
  }

  addScratchData(key) {
    this.scratchData[key] = true;
    return this;
  }
}

const isIntermediate = (component) => component === "intermediate-cable";

// Private helpers
const previousFilteredList = function (filterTag) {
  const idx = this.filterSequence.indexOf(filterTag);
  return idx === 0
    ? this.parts
    : this.filteredParts[this.filterSequence[idx - 1]];
};

const findAllowedCategories = function (
  partsList,
  categoryKey,
  categoryBits,
  filterBitMask = 0xffffffff,
) {
  // Create a bitmask of all possible categories in the partsCollection
  const allowedCategoryBits = partsList.reduce(
    (acc, part) => acc | part[categoryKey],
    0,
  );

  // return an array of keys to *_BITS that can be shown
  return Object.keys(categoryBits).reduce(
    (acc, key) =>
      allowedCategoryBits & categoryBits[key] & filterBitMask
        ? [...acc, key]
        : acc,
    [],
  );
};

const checkFilterRelevance = function (
  partsList,
  accessorySequence,
  filterTag,
) {
  let bitmask = -1;
  return partsList.some((part) => {
    if (
      !accessorySequence.find((component) =>
        sequenceComponentMatchesPart(component, part),
      )
    ) {
      return false;
    }
    // first item, set bitmask
    if (bitmask < 0) {
      bitmask = part[filterTag];
      return false;
    }
    // return true if we found a part that has a different bitmask than all the others
    return part[filterTag] !== bitmask;
  });
};

const findPartInList = function (partsList, partNo) {
  return partsList.find((part) => part.partNo === partNo);
};

const isAccessoryMatchable = function (
  partsList,
  accessory,
  categoryKey,
  categoryBit,
) {
  // Make sure the accessory has a part that can pair with it AND that part matches the filter provided
  // Matching part must be a terminal part that pairs with the accessory, or a terminal part that has a cable that
  // pairs with the accessory
  const a = partsList.find(
    (otherPart) =>
      otherPart.type === "terminal" &&
      otherPart[categoryKey] & categoryBit &&
      (otherPart.isPaired(accessory) ||
        (Array.isArray(otherPart.cables) &&
          otherPart.cables.some((cablePartNo) => {
            const cablePart = findPartInList(partsList, cablePartNo);
            return cablePart && cablePart.isPaired(accessory);
          }))),
  );
  if (accessory.partNo === "MP03027") {
    console.log(accessory, a);
  }
  return a;
};

const isAccessoryViable = function (accessoryPart) {
  // Look for a kit or terminal that is paired with this part
  return this.filteredList.find((otherPart) =>
    otherPart.isPaired(accessoryPart),
  );
};

const isConnectorMatchable = function (
  partsList,
  connectorPart,
  categoryKey,
  categoryBit,
) {
  // Make sure the cable has a part that can pair with it AND that part matches the filter provided
  console.log(
    "isConnectorMatchable",
    partsList,
    connectorPart,
    categoryKey,
    categoryBit,
  );
  return partsList.find(
    (otherPart) =>
      otherPart.type === "terminal" &&
      otherPart[categoryKey] & categoryBit &&
      otherPart.fitsCable(connectorPart),
  );
};

const isConnectorViable = function (connectorPart) {
  // Look for a kit or terminal that is paired with this part
  return this.filteredList.find(
    (otherPart) =>
      otherPart.type === "terminal" && otherPart.fitsCable(connectorPart),
  );
};

const sequenceComponentMatchesPart = function (component, part) {
  return typeof component === "string"
    ? component === part.type
    : component.type.indexOf(part.type) === 0;
};

export class PartsCollection {
  constructor() {
    this.filteredParts = {};
    this.filterSequence = [];
    this.parts = [];
    // TODO; possibly set up to cache parts by 'route', like 'ecg/3-lead/adult', etc.
  }

  set patientParameter(newPatientParameter) {
    this.filteredParts.patientParameter = previousFilteredList
      .call(this, "patientParameter")
      .filter(
        (part) =>
          newPatientParameter === "" ||
          part.patientParameter & PATIENT_PARAMETER_BITS[newPatientParameter],
      );
  }

  set parameterOption(newParameterOption) {
    this.filteredParts.parameterOption = previousFilteredList
      .call(this, "parameterOption")
      .filter(
        (part) =>
          newParameterOption === "" ||
          part.parameterOption & PARAMETER_OPTION_BITS[newParameterOption],
      );
  }

  set patientCategory(newPatientCategory) {
    this.filteredParts.patientCategory = previousFilteredList
      .call(this, "patientCategory")
      .filter(
        (part) =>
          newPatientCategory === "" ||
          part.patientCategory & PATIENT_CATEGORY_BITS[newPatientCategory],
      );
  }

  set usageCategory(newUsageCategory) {
    this.filteredParts.usageCategory = previousFilteredList
      .call(this, "usageCategory")
      .filter(
        (part) =>
          newUsageCategory === "" ||
          part.usageCategory & USAGE_CATEGORY_BITS[newUsageCategory],
      );
  }

  // Clear scratch data
  resetParts() {
    this.filteredList.forEach((part) => part.clearScratchData());
  }

  // For the patient parameter to be available,
  // there must be at least one part that identifies with it
  findAllowedPatientParameters(monitorPatientParameters) {
    return findAllowedCategories(
      this.parts,
      "patientParameter",
      PATIENT_PARAMETER_BITS,
    ).filter((patientParameter) =>
      monitorPatientParameters.includes(patientParameter),
    );
  }

  // For the parameter option to be available,
  // there must be at least one part that's first in the accessorySequence (accessory, kit, etc.)
  // that matches the option.
  // Need to handle special case where first item in accessorySequence is an intermediate-cable
  findAllowedParameterOptions(accessorySequence, includeOptions) {
    const partsList = previousFilteredList.call(this, "parameterOption");
    // if accessorySequence is an object, we need to merge results from calls to ourself using
    // all the keys
    // {
    //    "dräger smart pod connection": ["accessory", "terminal"],
    //    "dräger monitor connection": ["accessory"]
    //
    // }
    console.log(
      "findAllowedParameterOptions",
      accessorySequence,
      includeOptions,
    );
    if (!Array.isArray(accessorySequence)) {
      return Object.keys(accessorySequence).reduce(
        (acc, key) => [
          ...acc,
          ...this.findAllowedParameterOptions(accessorySequence[key], [key]),
        ],
        [],
      );
    }
    // sometimes we know only some parameter options will be available
    const filterMask = includeOptions
      ? includeOptions.reduce(
          (acc, parameterOption) =>
            acc | PARAMETER_OPTION_BITS[parameterOption],
          0,
        )
      : undefined;
    const allowedCategories = findAllowedCategories(
      partsList,
      "parameterOption",
      PARAMETER_OPTION_BITS,
      filterMask,
    );

    if (accessorySequence[0] === "intermediate-cable") {
      return allowedCategories.filter((parameterOption) =>
        accessorySequence.every((component) =>
          partsList.find(
            (part) =>
              part.type === component &&
              part.parameterOption & PARAMETER_OPTION_BITS[parameterOption] &&
              (component !== "intermediate-cable" ||
                isConnectorMatchable(
                  partsList,
                  part,
                  "parameterOption",
                  PARAMETER_OPTION_BITS[parameterOption],
                )),
          ),
        ),
      );
    }

    // normal case where accessory or kit is first
    const accessoryNeedsMatch =
      accessorySequence.includes("accessory") &&
      accessorySequence.includes("terminal");

    return allowedCategories.filter((parameterOption) =>
      accessorySequence.every(
        (component) =>
          isIntermediate(component) ||
          partsList.find(
            (part) =>
              part.type === component &&
              part.parameterOption & PARAMETER_OPTION_BITS[parameterOption] &&
              (component !== "accessory" ||
                !accessoryNeedsMatch ||
                isAccessoryMatchable(
                  partsList,
                  part,
                  "parameterOption",
                  PARAMETER_OPTION_BITS[parameterOption],
                )),
          ),
      ),
    );
  }

  // For the patient category to be available,
  // there must be at least one part in each component of the sequence that has the option
  findAllowedPatientCategories(accessorySequence) {
    const partsList = previousFilteredList.call(this, "patientCategory");
    const accessoryNeedsMatch =
      accessorySequence.includes("accessory") &&
      accessorySequence.includes("terminal");

    // if all parts match all patient categories, this filter is not relevant
    if (
      partsList.every(
        (part) => part.patientCategory === PATIENT_CATEGORY_FULL_MASK,
      )
    ) {
      return [];
    }

    // if all parts in the sequence have the same bits set, this filter is not relevant
    const isRelevant = checkFilterRelevance(
      partsList,
      accessorySequence,
      "patientCategory",
    );
    if (!isRelevant) {
      return [];
    }

    // ... find which categories are allowed
    return findAllowedCategories(
      partsList,
      "patientCategory",
      PATIENT_CATEGORY_BITS,
    ).filter((patientCategory) =>
      accessorySequence.every(
        (component) =>
          isIntermediate(component) ||
          partsList.find(
            (part) =>
              sequenceComponentMatchesPart(component, part) &&
              part.patientCategory & PATIENT_CATEGORY_BITS[patientCategory] &&
              (component !== "accessory" ||
                !accessoryNeedsMatch ||
                isAccessoryMatchable(
                  partsList,
                  part,
                  "patientCategory",
                  PATIENT_CATEGORY_BITS[patientCategory],
                )),
          ),
      ),
    );
  }

  // For the usage category to be available,
  // there must be at least one part in each component of the sequence that has the option
  findAllowedUsageCategories(accessorySequence) {
    const partsList = previousFilteredList.call(this, "usageCategory");
    const accessoryNeedsMatch =
      accessorySequence.includes("accessory") &&
      accessorySequence.includes("terminal");
    console.log("findAllowedUsageCategories", accessorySequence);

    // if all parts match all usage categories, this filter is not relevant
    if (
      partsList.every((part) => part.usageCategory === USAGE_CATEGORY_FULL_MASK)
    ) {
      return [];
    }

    // if all parts in the sequence have the same bits set, this filter is not relevant
    const isRelevant = checkFilterRelevance(
      partsList,
      accessorySequence,
      "usageCategory",
    );
    if (!isRelevant) {
      return [];
    }

    return findAllowedCategories(
      partsList,
      "usageCategory",
      USAGE_CATEGORY_BITS,
    ).filter((usageCategory) =>
      accessorySequence.every(
        (component) =>
          isIntermediate(component) ||
          partsList.find(
            (part) =>
              sequenceComponentMatchesPart(component, part) &&
              part.usageCategory & USAGE_CATEGORY_BITS[usageCategory] &&
              (component !== "accessory" ||
                !accessoryNeedsMatch ||
                isAccessoryMatchable(
                  partsList,
                  part,
                  "usageCategory",
                  USAGE_CATEGORY_BITS[usageCategory],
                )),
          ),
      ),
    );
  }

  findPart(partNo, useAll = false) {
    const partsList = useAll ? this.parts : this.filteredList;
    return findPartInList(partsList, partNo);
  }

  findPartsWithGrouping(grouping) {
    return this.filteredList.filter(
      (part) => part.group && part.group === grouping,
    );
  }

  findAccessoryOptions(accessorySequence) {
    // This is basically just for the ESU blocks & connection cables
    const findConnectedOptionalParts = (part) => {
      const optionalParts = this.filteredList.filter(
        (otherPart) =>
          (typeof otherPart.type === "undefined" || otherPart.type === "") &&
          otherPart.isPaired(part),
      );
      // mark these as temporarily optional
      optionalParts.forEach((optionalPart) =>
        optionalPart.addScratchData("optional"),
      );
      return optionalParts;
    };

    const accessoryNeedsViabilityCheck = accessorySequence.includes("terminal");
    return this.filteredList
      .filter(
        (part) =>
          part.type === "accessory" &&
          (!accessoryNeedsViabilityCheck || isAccessoryViable.call(this, part)),
      )
      .reduce(
        (acc, part) => [
          ...acc,
          part.cables === "required" ? part.addScratchData("disabled") : part,
          ...findConnectedOptionalParts(part).map((optionalPart) => [
            part,
            optionalPart,
          ]),
        ],
        [],
      );
  }

  findConnectorOptions(accessoryPart, filterChoices) {
    // hard code this for now...
    // cable is optional if
    // - ecg + adult,pedi
    // - temp
    // - spO2 and no accessoryPart given (Vista 120 family)
    const { patientParameter } = filterChoices;
    const optional =
      (patientParameter === "ecg" &&
        filterChoices.patientCategory !== "neonatal") ||
      patientParameter === "temp" ||
      (patientParameter === "spo2" && !accessoryPart);

    // find all connectors that are paired with the part, or have no specific pairing
    const cableOptions = this.filteredList
      .filter(
        (part) =>
          part.type === "intermediate-cable" &&
          isConnectorViable.call(this, part),
      )
      .filter(
        (connector) =>
          !connector.pairedParts ||
          !accessoryPart ||
          connector.isPaired(accessoryPart),
      );

    // if no connectors available, give none as an option
    if (cableOptions.length === 0 || optional) {
      // only allowed if accessory part exists OR some terminals don't need a cable
      // eg. Vista 120 > spO2 > Nellcor > no terminals that don't need cables
      if (
        accessoryPart ||
        this.filteredList.some(
          (part) => part.type === "terminal" && !part.cables,
        )
      ) {
        cableOptions.push(
          cableOptions.length > 0 && cableOptions[0].group
            ? `no-${cableOptions[0].group}`
            : "no-extension-cable",
        );
      }
    }
    return cableOptions;
  }

  findKitOptions(precedingPart) {
    // Find all kits; if precedingPart is provided, the kit must be paired with it
    const kits = this.filteredList.filter(
      (part) =>
        part.type === "kit" && (!precedingPart || part.isPaired(precedingPart)),
    );
    return kits.reduce(
      (options, kitPart) => [
        ...options,
        kitPart.addScratchData("underline"),
        ...this.collectPartsFromPartNumbers(kitPart.includedParts),
      ],
      [],
    );
  }

  findTerminalOptions(accessoryPart, cableOption, parameterOptionFilter) {
    // if no accessory part is given, find terminal parts that connect directly to the monitor
    const cablePart = cableOption instanceof Part ? cableOption : null;
    if (!accessoryPart) {
      // we do have a cable here, it connects straight to the monitor, so find terminal parts for it
      if (cablePart) {
        return this.filteredList.filter(
          (terminalPart) =>
            terminalPart.type === "terminal" &&
            terminalPart.fitsCable(cablePart),
        );
      } else if (cableOption === "no-extension-cable") {
        // Case of: Vista 120 > Spo2 > Drager
        // User may choose no extension cable
        return this.findDirectlyConnectedTerminalParts(parameterOptionFilter);
      }

      return this.findDirectlyConnectedTerminalOptions(parameterOptionFilter);
    }

    // Find terminals that fit the cable and/or pair with the accessory or cable that pairs with the accessory
    return this.filteredList.filter(
      (terminalPart) =>
        terminalPart.type === "terminal" &&
        terminalPart.fitsCable(cablePart) &&
        (terminalPart.isPaired(accessoryPart) ||
          (cablePart && cablePart.isPaired(accessoryPart))),
    );
  }

  findDirectlyConnectedTerminalParts(parameterOptionFilter) {
    // check if part has the parameter options specified by the filter and has no cables it needs
    return this.filteredList.filter(
      (part) =>
        part.type === "terminal" &&
        matchesOptionFilter(part, parameterOptionFilter) &&
        !part.cables,
    );
  }

  findDirectlyConnectedTerminalOptions(parameterOptionFilter) {
    // For terminals that plug directly into the monitor (ie. ECG leads -> M540)
    const findConnectedOptionalCables = (part) =>
      Array.isArray(part.cables)
        ? part.cables.reduce((acc, partNo) => {
            const cablePart = this.findPart(partNo);
            if (cablePart) {
              // used to include && cablePart.group !== 'extension-cable'... why??
              // mark these as temporarily optional
              cablePart.addScratchData("optional");
              return [...acc, cablePart];
            }
            return acc;
          }, [])
        : [];

    // check if part has the parameter options specified by the filter
    return this.filteredList
      .filter(
        (part) =>
          part.type === "terminal" &&
          matchesOptionFilter(part, parameterOptionFilter),
      )
      .reduce(
        (acc, part) => [
          ...acc,
          part.cables === "required" ? part.addScratchData("disabled") : part,
          ...findConnectedOptionalCables(part).map((optionalPart) => [
            part,
            optionalPart,
          ]),
        ],
        [],
      );
  }

  findCompatibleAdapters(accessoryPart, terminalPart) {
    const adapters = this.filteredList.filter(
      (part) => part.type === "adapter",
    );
    return adapters.filter(
      (adapter) =>
        accessoryPart.isPaired(adapter) &&
        (!terminalPart || adapter.group === terminalPart.group),
    );
  }

  findMonitorConnectionOptions(accessoryParts) {
    // if part has no type, look for pairings, otherwise return the group for that part
    return accessoryParts.reduce((connections, part) => {
      if (!part || !(part instanceof Part)) {
        return connections;
      }
      // if part has a monitor connection defined, use that and put it first
      if (part.monitorConnection) {
        return [part.monitorConnection, ...connections];
      }
      // if part has a type, use the group for that part
      if (part.type) {
        return [...connections, part.group];
      }
      // look at paired parts and use their group if found
      if (part.pairedParts) {
        const foundPairing = part.pairedParts.find((otherPartNo) => {
          const pairedPart = this.findPart(otherPartNo);
          return (
            pairedPart && (pairedPart.group || pairedPart.monitorConnection)
          );
        });
        if (foundPairing) {
          return foundPairing.monitorConnection
            ? [foundPairing.monitorConnection, ...connections]
            : [...connections, foundPairing.group];
        }
      }
      // otherwise, return the group for this part
      return [...connections, part.group];
    }, []);
  }

  collectPartsFromPartNumbers(partNumbersList) {
    // TODO - result should be memoized
    return partNumbersList.reduce(
      (partCollection, partNo) => [
        ...partCollection,
        ...[this.findPart(partNo, true)].filter((part) => part),
      ],
      [],
    );
  }

  bundleIncludedParts(part, parameterOption) {
    const bundle = [];
    // kits are just by themselves
    if (part.type === "kit" && part.isOpaqueKit()) {
      return [part.partNo];
    }
    // include the part itself except for the 1 'non-opaque' kit for EEG
    if (part.type !== "kit") {
      bundle.push(part.partNo);
    }
    if (typeof part.includedParts !== "undefined") {
      part.includedParts.forEach((includedPartNo) => {
        const includedPart = this.findPart(includedPartNo, true);
        if (!includedPart) {
          console.error("Included part not found!!!", part, includedPartNo);
        }
        if (
          includedPart.parameterOption === 0 ||
          includedPart.parameterOption &
            PARAMETER_OPTION_BITS[parameterOption] ||
          part.type === "kit"
        ) {
          bundle.push(includedPartNo);
        }
      });
    }
    return bundle;
  }

  get filteredList() {
    return this.filterSequence.reduce(
      (acc, key) => this.filteredParts[key] || acc,
      this.parts,
    );
  }
}
